import type { ListScheduleEventsQuery } from '@sportninja/common/services/event/listScheduleEvents'

export interface BuildCompetitionEventsQueryArgs {
  /** `sn_team` — the team filter, NOT a `team_id` prop. See the note below. */
  teamId?: string | null
  venueId?: string | null
  facilityId?: string | null
}

/**
 * Sized to fetch a competition's events in one round-trip. The service still
 * paginates past it, so this is a trip-count optimisation rather than a cap.
 */
const EVENTS_PER_PAGE = 200

/**
 * The filters for a competition's events, sent to `/schedules/{id}/events`.
 *
 * The schedule itself is the PATH, not a filter, so it is not built here — the
 * caller passes it to the service directly.
 *
 * Deliberately sends NO date window. The Schedule list filters events
 * client-side for its Past / Today / Upcoming tabs, and
 * `mergeEventsIntoDayBuckets` assumes the list it merges is complete — a
 * server-side window would hide events the client then believes do not exist.
 */
export const buildCompetitionEventsQuery = ({
  teamId,
  venueId,
  facilityId,
}: BuildCompetitionEventsQueryArgs): ListScheduleEventsQuery => {
  const query: ListScheduleEventsQuery = {
    per_page: EVENTS_PER_PAGE,
    // A competition- or season-level schedule has to reach the events hanging
    // off its children; without this a season would answer only for events
    // pinned to the season row itself, which is almost none of them.
    recursive: 1,
  }

  // Unlike the Team page — which scopes events to its `team_id` PROP — the
  // competition page has no team of its own, so the team FILTER is the only
  // team signal there is. Sending it keeps events narrowing in step with the
  // games list, which takes the same value.
  if (teamId) {
    query.team_id = teamId
  }
  if (venueId) {
    query.venue_id = venueId
  }
  if (facilityId) {
    query.facility_id = facilityId
  }

  return query
}
